// 👥 Employés E4b — Section 6 : Documents & permis. Bloc Documents gaté par `contrats`, bloc Permis par `permis`
// (lecture ET édition : toggles/ajout/suppression réservés au propriétaire).
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, Key, Plus, RefreshCw, Trash2, XCircle } from 'lucide-react';
import { SectionFiche } from './SectionFiche';
import { EmployeeContrats } from '@/components/EmployeeContrats';
import { PermisConduirePhotosBlock } from '@/components/PermisConduirePhotosBlock';

export function SectionDocuments({ employee, isProprietaire, voirContrats, voirPermis, onEmployeeRefresh, permis }) {
  const nomComplet = `${employee.prenom} ${employee.nom}`;
  return (
    <SectionFiche cle="documents" titre="Documents & permis" icone={FileText}>
      <div className="space-y-4">
        {voirContrats && <div data-testid="bloc-documents"><EmployeeContrats employeeId={employee.id} employeeNom={nomComplet} /></div>}
        {voirPermis && (
          <div className="space-y-4" data-testid="bloc-permis">
            <PermisConduirePhotosBlock employee={employee} canEdit={isProprietaire} onUpdate={onEmployeeRefresh} />
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Key className="h-5 w-5 text-amber-600" />Permis opérationnels</CardTitle>
                <p className="text-xs text-slate-500">{isProprietaire ? 'Activez ou désactivez les permis pour cet employé' : 'Permis accordés à cet employé'}</p>
              </CardHeader>
              <CardContent className="space-y-2">
                {permis.loading ? <div className="text-center py-6 text-slate-400 text-sm">Chargement...</div>
                  : permis.types.length === 0 ? <div className="text-center py-6 text-slate-400 text-sm italic">Aucun permis configuré</div>
                  : permis.types.map((pt) => {
                    const accord = (employee.permis_accordes || []).find((p) => p.permis_id === pt.id);
                    const hasPermis = !!accord;
                    const isToggling = permis.toggling === pt.id;
                    return (
                      <div key={pt.id} data-testid={`permis-row-${pt.id}`} className={`flex items-center justify-between p-3 rounded-lg border transition-all ${hasPermis ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}>
                        <div className="flex items-center gap-3">
                          {hasPermis ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />}
                          <div>
                            <p className={`font-medium text-sm ${hasPermis ? 'text-emerald-900' : 'text-slate-600'}`}>{pt.nom}</p>
                            {accord && <p className="text-xs text-emerald-600 mt-0.5">Accordé le {accord.accordee_le} par {accord.accordee_par}</p>}
                          </div>
                        </div>
                        {isProprietaire && (
                          <div className="flex items-center gap-2">
                            <button data-testid={`toggle-permis-${pt.id}`} onClick={() => permis.onToggle(pt.id, pt.nom, hasPermis)} disabled={isToggling}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${hasPermis ? 'bg-emerald-500' : 'bg-slate-300'} ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${hasPermis ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                            <button onClick={() => permis.onDelete(pt.id, pt.nom)} className="text-slate-300 hover:text-red-500 transition-colors p-1" title="Supprimer ce type de permis" data-testid={`delete-permis-type-${pt.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </CardContent>
            </Card>
            {isProprietaire && (
              <Card className="border-dashed">
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2">Ajouter un type de permis</p>
                  <div className="flex gap-2">
                    <Input data-testid="input-new-permis-nom" placeholder="Ex: Opérer une mini-pelle, Travail en hauteur..." value={permis.nouveauNom} onChange={(e) => permis.setNouveauNom(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && permis.onAjouter()} className="text-sm" />
                    <Button data-testid="btn-add-permis-type" onClick={permis.onAjouter} disabled={permis.ajoutEnCours || !permis.nouveauNom.trim()} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap">
                      {permis.ajoutEnCours ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">Ce permis sera disponible pour tous les employés</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </SectionFiche>
  );
}
